/**
 * Item rows for one member order of the "Add items to the RMA" table.
 * Emits bare rows (no wrapper element) so that every fulfillment group shares the single table
 * owned by `OrderProductListCard`.
 */
import {observer} from 'mobx-react-lite';
import React from 'react';
import AHref from "../../../components/ahref/ahref";
import {FontBody2} from "../../../components/fonts/font-body2/font-body2";
import FontBody4 from "../../../components/fonts/font-body4/font-body4";
import {FONT_COLOR, FONT_VARIANT} from "../../../components/fonts/font-props";
import IconInfo from "../../../components/icons/icon-info/icon-info";
import IconPlus from "../../../components/icons/icon-plus/icon-plus";
import IconSirenOnLight from "../../../components/icons/icon-siren-on-light/icon-siren-on-light";
import Icon from "../../../components/icons/icon/icon";
import {ButtonIcon} from "../../../components/inputs/button-icon/button-icon";
import TableRow from "../../../components/table/components/table-row/table-row";
import TextTruncate from "../../../components/text-truncate/text-truncate";
import {Tooltip} from "../../../components/tooltip/tooltip";
import config from "../../../config";
import useDashboardTranslation from "../../../dashboard-translation/hooks/dashboard-translation-hook";
import CreateRmaSettingsStore from "../stores/create-rma-settings-store";
import currentPortalStore from "../../../stores/current-portal-store";
import OrderProductStore, { PRODUCT_STATUS_MAP } from "../stores/order-product-store";
import './order-product-list-card.scss';
import AddItemDrawer from "./add-item-drawer";
import RmaRequestStore from '../stores/rma-request-store';
import OrderStore from '../stores/order-store';
import Currency from "../../../components/currency/currency";
import { CREATE_RMA_MODE } from '../../../services/api/create-rma-service';
import { getPlatformIconComponent } from '../../../components/icons/icon-platform/icon-platform';
import { PLATFORM_DISPLAY_NAME } from '../../../stores/platform-service-store';
import DateTimeV2, { DATETIME_VIEW } from '../../../components/datetime-v2/datetime-v2';
import { useDateTimeFormattingParams } from '../../../hooks/use-datetime-formatting-params';
import { PRODUCT_STATUS_ENUM } from '../../../services/order-service';
import FontBody3 from '../../../components/fonts/font-body3/font-body3';

interface IProps {
	rmaRequestStore: RmaRequestStore;
	orderStore: OrderStore;
    createRmaSettingsStore: CreateRmaSettingsStore;
    isIPad: boolean;
    isRMACreated?: boolean;
}

interface IAddButtonProps {
    onClick: () => void;
    disabled: boolean;
}

const ButtonAddItem: React.FC<IAddButtonProps> = (({onClick, disabled}) => {
    return (
        <ButtonIcon onClick={onClick} Component={ IconPlus} disabled={disabled}/>
    );
});

const ButtonOrderLink: React.FC<{ orderStore: OrderStore }> = (({orderStore}) => {
    /** don't render platorm button-icon next to line items for non shopify platforms */
    if (currentPortalStore.platform?.getPlatformDisplayName() !== PLATFORM_DISPLAY_NAME.SHOPIFY) {
        return null;
    }

    const iconComponent = getPlatformIconComponent(currentPortalStore.platform?.getPlatformDisplayName());
    return (
        <ButtonIcon href={orderStore.getShopifyOrderLink()} target={'_blank'}  Component={iconComponent}/>
    );
});

const OrderProductListSection: React.FC<IProps> = observer(({ rmaRequestStore, orderStore, createRmaSettingsStore, isIPad, isRMACreated = false }) => {
    
    const { getTranslation } = useDashboardTranslation();
    const pageScope = 'dashboard-create-rma';
    const dateTimeParams = useDateTimeFormattingParams();
    const isGodMode = createRmaSettingsStore.cRmaMode === CREATE_RMA_MODE.C_RMA_GOD_MODE;
    const showOutOfPolicyIcon = (product: OrderProductStore):boolean => { 
        return orderStore.isCreateRmaNotAllowed || (!product.isCustomItem && orderStore.isLineItemIdOutOfPolicy(product.lineItemId!))};

    /**
     * In god mode, rejected items are treated as re-returnable — show their Shopify fulfillment
     * status (Fulfilled / Unfulfilled) so the row looks like any other returnable item.
     * In policy-led mode, show the RMA-domain status (Rejected) so the merchant can see why the
     * item cannot be added.
     */
    const getDisplayStatus = (product: OrderProductStore) => {
        /** Return-blocked items (virtual / dangerous goods) surface their own status chip regardless of mode. */
        if (product.returnBlockedStatus) {
            return product.returnBlockedStatus;
        }
        return product.isRejected && isGodMode ? product.fulfillmentStatus : product.status;
    };

    const showRmaUi = (product: OrderProductStore) =>
        !!product.rma && !(product.isRejected && isGodMode);

    const getStatusCellContent = (product: OrderProductStore) => {
        const status = showRmaUi(product) ? PRODUCT_STATUS_MAP[PRODUCT_STATUS_ENUM.RETURNED] : getDisplayStatus(product);
        return (
            <span className={status.class + ' chip'}>
                {getTranslation(status.textTranslationKey, pageScope)}
            </span>
        );
    };

    const isAddItemDisabled = (lineItemId: string) => {
        if (isRMACreated) {
            return true;
        }

        /** Unlimited (god) mode ignores per-item out-of-policy and the "multiple return requests" blocker */
        if (isGodMode) {
            return false;
        }
        return orderStore.isLineItemIdOutOfPolicy(lineItemId) || orderStore.isCreateRmaNotAllowed;
    }

	return (
        <>
            {/* PHASE 2: add search bar*/}
            {
                    !!orderStore &&
                    orderStore.productList.map((product: OrderProductStore) => {
                        let compareAtPrice = <></>;
                        if(product.compareAtPrice() !== null){
                            compareAtPrice = (
                                <FontBody2 variant={FONT_VARIANT.LINE_TROUGH}>
                                    <Currency currencyCode={product.paymentCurrencyCode} amount={product.compareAtPrice()!}/>
                                </FontBody2>
                            );
                        }
                        let tableRow=[
                            showOutOfPolicyIcon(product) ? <Tooltip
                            content={
                                <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                    {getTranslation('ITEM_OUT_OF_POLICY_INDICATOR_TEXT', pageScope)}
                                </FontBody2>
                            }
                        >
                            <Icon Component={IconSirenOnLight} />
                        </Tooltip> : <></>,
                            <img alt="" className="product-image" src={product.imageUrl}/>,
                            <>
                                <div>
                                    <TextTruncate>
                                        <AHref href={product.variantUrl} target={'_blank'}>
                                            <FontBody2 color={FONT_COLOR.PRIMARY2}>{product.name}</FontBody2>
                                        </AHref>
                                    </TextTruncate>
                                </div>
                                <TextTruncate>
                                    <FontBody2 color={FONT_COLOR.NEUTRAL3}>{product.variantTitle}</FontBody2>
                                </TextTruncate>
                            </>,
                            <FontBody2 color={FONT_COLOR.PRIMARY2}>{rmaRequestStore.getOrderProductAvailableQuantity(product)}</FontBody2>,
                            <FontBody2 color={FONT_COLOR.PRIMARY2}>
                                <Currency currencyCode={product.paymentCurrencyCode} amount={rmaRequestStore.getOrderProductTotalPaidAmount(product)}/>
                                { rmaRequestStore.getOrderProductTotalPaidAmount(product) !== product.paidAmount && rmaRequestStore.getOrderProductAvailableQuantity(product) > 0 &&
                                    <>
                                        /
                                        <FontBody4>
                                            <Currency currencyCode={product.paymentCurrencyCode} amount={product.paidAmount}/>
                                        </FontBody4>
                                    </>
                                }
                            </FontBody2>,
                            <FontBody2 color={FONT_COLOR.PRIMARY2}>
                                <div className={'compare-at-price-flex'}>
                                    <Currency currencyCode={product.paymentCurrencyCode} amount={product.catalogPrice}/>
                                    {compareAtPrice}
                                </div>
                            </FontBody2>,
                            <FontBody2 color={FONT_COLOR.PRIMARY2}>
                                {product.fulfillmentDate ? <DateTimeV2 {...dateTimeParams} utcDateString={product.fulfillmentDate} dateTimeView={DATETIME_VIEW.DATE_ONLY} /> : ''}
                            </FontBody2>,
                            getStatusCellContent(product),
                    showRmaUi(product)
                        //if: item has RMA; in policy-led mode rejected items show RMA link; in god mode they fall through to Add button
                        ? <span className="rma-action-link">
                            <AHref href={config.apiUrl + '/dashboard/returns/?rmaid=' + product.rma!.id} target={'_blank'}>RMA{product.rma!.id}</AHref>
                            <FontBody3 color={FONT_COLOR.PRIMARY3_50}>
                                ({getTranslation(product.rma!.status, 'dashboard-returns-page')})
                            </FontBody3>
                          </span>
                                :
                                //else if
                                product.isReturnBlocked
                                    ?
                                    <Tooltip content={
                                        <div className={'order-product-list-card-tooltip'}>
                                            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{getTranslation(product.isReturnBlockedDangerousGood ? 'DANGEROUS_GOOD_NOT_RETURNABLE' : 'VIRTUAL_GOOD_NOT_RETURNABLE', pageScope)}</FontBody2>
                                        </div>
                                    }>
                                        <IconInfo/>
                                    </Tooltip>
                                :
                                //else if
                                product.isCustomItem
                                    ?
                                    <Tooltip content={
                                        <div className={'order-product-list-card-tooltip'}>
                                            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{getTranslation('CUSTOM_ITEM_PREVENTION', pageScope)}</FontBody2>
                                        </div>
                                    }>
                                        <IconInfo/>
                                    </Tooltip>
                                :
                                //else if
                                product.hasBxGy
                                    ?
                                    <Tooltip content={
                                        <div className={'order-product-list-card-tooltip'}>
                                            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{getTranslation('BXGY_PREVENTION', pageScope)}</FontBody2>
                                        </div>
                                    }>
                                        <IconInfo/>
                                    </Tooltip>
                                : //else
                                <>
                                    { currentPortalStore.allowUnfulfilled || (product.isFulfilled() && !product.isShopifyReturned) ?
                                        <ButtonAddItem onClick={() => product.setShowAddToRma(true)} disabled={isAddItemDisabled(product.lineItemId!)}/>
                                        : <ButtonOrderLink orderStore={orderStore}/>
                                    }
                                    { product.showAddToRma &&
                                        <AddItemDrawer request={rmaRequestStore} createRmaSettingsStore={createRmaSettingsStore} requestProduct={rmaRequestStore.getProductRequestToEdit(product)} closeAction={()=>{product.setShowAddToRma(false)}}/>
                                    }
                                </>
                            //endif
                        ];
                        if(isIPad){
                            tableRow.splice(5,2);
                        }
                        return <TableRow key={product.uuid}
                                      disabled={rmaRequestStore.getOrderProductAvailableQuantity(product) === 0}
                                      cells={tableRow}
                                      dataAttributes={{
                                          'data-testid': 'order-product-row',
                                          'data-order-id': orderStore.orderId,
                                      }}
                            />
                            
                        }
                    )}
        </>
	);
});

export default OrderProductListSection;
